
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import './Messages.css';
import {
  MdCalendarMonth,
  MdEmail,
  MdPhone,
  MdBusiness,
  MdLocationOn,
} from 'react-icons/md';
import { RiDeleteBin5Fill, RiReplyAllFill } from 'react-icons/ri';
import axiosInstance from '../../utils/axiosInstance';

const REFRESH_MS = 20000;

// Last loaded messages of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const MESSAGES_CACHE_PREFIX = 'messages_cache_';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {}
};

const toast = (icon, title) =>
  Swal.fire({
    icon,
    title,
    toast: true,
    position: 'top-end',
    timer: 2200,
    showConfirmButton: false,
  });

const errorText = (err, fallback) =>
  err?.response?.data?.error ||
  err?.response?.data?.detail ||
  fallback;

const formatDate = (iso) => {
  if (!iso) return '';

  const date = new Date(iso);

  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
};

const Messages = () => {
  const { shopId } = useParams();
  const cacheKey = `${MESSAGES_CACHE_PREFIX}${shopId || 'all'}`;

  // The shop is known from the very first render, and the saved list of that shop
  // is shown instantly while fresh data loads in the background.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    const cached = readJson(cacheKey);
    initialRef.current = Array.isArray(cached) ? cached : null;
  }

  const [messages, setMessages] = useState(initialRef.current || []);
  // True only while there is nothing to show yet (so "No messages yet" never flashes)
  const [isLoading, setIsLoading] = useState(!initialRef.current);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const requestRef = useRef(0);
  const prevKeyRef = useRef(shopId || 'all');

  // local changes (read / delete / reply) are saved too, so the saved copy is always correct
  const updateMessages = (updater) => {
    setMessages((previous) => {
      const next = updater(previous);
      writeJson(cacheKey, next);
      return next;
    });
  };

  const load = useCallback(
    async (silent = false) => {
      // Only the latest request is allowed to update the screen
      const requestId = ++requestRef.current;
      try {
        const params = {};

        if (shopId) {
          params.shop_id = shopId;
        }

        const response = await axiosInstance.get(
          '/feedback/messages/',
          { params }
        );
        if (requestId !== requestRef.current) return;

        const data = response.data;

        if (Array.isArray(data)) {
          setMessages(data);
          writeJson(cacheKey, data);
          setError('');
        } else {
          setMessages([]);
          setError('Unexpected response received from the server.');
        }
      } catch (err) {
        if (requestId !== requestRef.current) return;

        const message = errorText(
          err,
          'Could not load messages. Please check your connection.'
        );

        if (!silent) {
          setError(message);
        }

        console.error(
          'Failed to load contact messages:',
          err.response?.status,
          err.response?.data || err.message
        );
      } finally {
        if (requestId === requestRef.current) setIsLoading(false);
      }
    },
    [shopId, cacheKey]
  );

  useEffect(() => {
    const key = shopId || 'all';
    // Shop changed: show the saved list of this shop immediately (if there is one)
    if (prevKeyRef.current !== key) {
      prevKeyRef.current = key;
      const cached = readJson(cacheKey);
      if (Array.isArray(cached)) {
        setMessages(cached);
        setIsLoading(false);
      } else {
        setMessages([]);
        setIsLoading(true);
      }
    }

    load();

    const timer = setInterval(() => {
      load(true);
    }, REFRESH_MS);

    const onFocus = () => load(true);

    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const unreadCount = useMemo(
    () => messages.filter((message) => !message.is_read).length,
    [messages]
  );

  const visible = useMemo(() => {
    if (filter === 'unread') {
      return messages.filter((message) => !message.is_read);
    }

    if (filter === 'account') {
      return messages.filter((message) => message.request_account);
    }

    return messages;
  }, [messages, filter]);

  const setRead = async (message, isRead) => {
    updateMessages((previous) =>
      previous.map((item) =>
        item.id === message.id
          ? { ...item, is_read: isRead }
          : item
      )
    );

    try {
      await axiosInstance.patch(
        `/feedback/messages/${message.id}/`,
        { is_read: isRead }
      );
    } catch (err) {
      toast(
        'error',
        errorText(err, 'Could not update the message.')
      );

      load(true);
    }
  };

  const handleDelete = async (message) => {
    const result = await Swal.fire({
      title: 'Delete this message?',
      text: `${message.name} - ${message.query_label}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(
        `/feedback/messages/${message.id}/`
      );

      updateMessages((previous) =>
        previous.filter((item) => item.id !== message.id)
      );

      toast('success', 'Message deleted');
    } catch (err) {
      toast(
        'error',
        errorText(err, 'Could not delete the message.')
      );
    }
  };

  const handleReply = (message) => {
    setSelectedMessage(message);
    setReplyText('');
    setShowReplyModal(true);

    if (!message.is_read) {
      setRead(message, true);
    }
  };

  const closeModal = () => {
    if (sending) return;

    setShowReplyModal(false);
    setSelectedMessage(null);
    setReplyText('');
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast('warning', 'Please enter a reply message');
      return;
    }

    setSending(true);

    try {
      const response = await axiosInstance.post(
        `/feedback/messages/${selectedMessage.id}/reply/`,
        { reply: replyText.trim() }
      );

      updateMessages((previous) =>
        previous.map((message) =>
          message.id === response.data.id
            ? response.data
            : message
        )
      );

      toast('success', `Reply sent to ${selectedMessage.email}`);

      setShowReplyModal(false);
      setSelectedMessage(null);
      setReplyText('');
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Reply not sent',
        text: errorText(err, 'Something went wrong.'),
        confirmButtonColor: '#004aad',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="messages-uk-container">
      <div className="messages-header">
        <h2>
          <MdEmail className="messages-title-icon" /> Messages
          {unreadCount > 0 && (
            <span className="comm-count-badge">
              {unreadCount}
            </span>
          )}
        </h2>
      </div>

      <div className="comm-toolbar">
        <button
          className={`comm-chip ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({messages.length})
        </button>

        <button
          className={`comm-chip ${filter === 'unread' ? 'active' : ''}`}
          onClick={() => setFilter('unread')}
        >
          Unread ({unreadCount})
        </button>

        <button
          className={`comm-chip ${filter === 'account' ? 'active' : ''}`}
          onClick={() => setFilter('account')}
        >
          Account requests (
          {messages.filter((message) => message.request_account).length}
          )
        </button>

        <span className="comm-live">
          <span className="comm-live-dot" />
          Live - updates every 20 s
        </span>
      </div>

      {error && <div className="comm-error">{error}</div>}

      <div className="messages-grid">
        {/* Nothing is shown until the first load is done, so "No messages yet" never flashes */}
        {isLoading ? (
          <div style={{ minHeight: '200px', gridColumn: '1 / -1' }} />
        ) : visible.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📭</div>
            <p className="empty-state-text">
              No messages yet
            </p>
          </div>
        ) : (
          visible.map((message) => (
            <div
              key={message.id}
              className="message-card"
            >
              <div className="message-card-header">
                <div className="message-avatar">
                  {(message.name || '?')
                    .charAt(0)
                    .toUpperCase()}
                </div>

                <div className="message-user-info">
                  <h3 className="message-user-name">
                    {!message.is_read && (
                      <span
                        className="comm-unread-dot"
                        title="Unread"
                      />
                    )}
                    {message.name}
                  </h3>

                  <p className="message-user-email">
                    {message.email}
                  </p>

                  <p className="message-date">
                    <span>
                      <MdCalendarMonth />
                    </span>
                    {formatDate(message.created_at)}
                  </p>
                </div>
              </div>

              <div className="message-content">
                <div className="comm-tags">
                  <span
                    className={`comm-tag ${
                      message.request_account ? 'account' : ''
                    }`}
                  >
                    {message.query_label}
                  </span>

                  {message.shop_name && (
                    <span className="comm-tag shop">
                      {message.shop_name}
                    </span>
                  )}
                </div>

                {(message.phone || message.company) && (
                  <div className="comm-meta">
                    {message.phone && (
                      <span>
                        <MdPhone /> {message.phone}
                      </span>
                    )}

                    {message.company && (
                      <span>
                        <MdBusiness /> {message.company}
                      </span>
                    )}
                  </div>
                )}

                <div className="message-text">
                  {message.message}
                </div>

                {message.request_account && message.address && (
                  <div className="comm-address">
                    <strong>
                      <MdLocationOn
                        style={{ verticalAlign: 'middle' }}
                      />{' '}
                      Address:
                    </strong>{' '}
                    {message.address}
                  </div>
                )}

                {message.reply_text && (
                  <div className="comm-replied">
                    <small>
                      Replied {formatDate(message.replied_at)}
                    </small>
                    {message.reply_text}
                  </div>
                )}

                <div className="message-actions">
                  <button
                    className="message-reply-btn"
                    onClick={() => handleReply(message)}
                  >
                    <RiReplyAllFill />{' '}
                    {message.reply_text
                      ? 'Reply again'
                      : 'Reply'}
                  </button>

                  {!message.is_read && (
                    <button
                      className="message-read-btn"
                      onClick={() => setRead(message, true)}
                    >
                      Mark read
                    </button>
                  )}

                  <button
                    className="message-delete-btn"
                    onClick={() => handleDelete(message)}
                  >
                    <RiDeleteBin5Fill /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showReplyModal && selectedMessage && (
        <div
          className="modal-overlay"
          onClick={closeModal}
        >
          <div
            className="modal-content"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>
              Reply to {selectedMessage.name}
            </h3>

            <p className="modal-subject">
              To: {selectedMessage.email} -{' '}
              {selectedMessage.query_label}
            </p>

            <div className="comm-original">
              {selectedMessage.message}
            </div>

            <textarea
              className="modal-textarea"
              rows="5"
              placeholder="Type your reply here... (sent to the customer by e-mail)"
              value={replyText}
              onChange={(event) =>
                setReplyText(event.target.value)
              }
            />

            <div className="modal-buttons">
              <button
                className="modal-cancel-btn"
                onClick={closeModal}
                disabled={sending}
              >
                Cancel
              </button>

              <button
                className="modal-send-btn"
                onClick={handleSendReply}
                disabled={sending}
              >
                {sending ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;